import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CalendarDays, Clock, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { getBlogArticleBySlug, getRelatedBlogArticles } from '../data/blogArticles';
import { SITE_URL, absoluteUrl, getBlogArticleImagePath } from '../utils/seo';

export default function BlogArticlePage() {
  const { slug } = useParams();
  const article = getBlogArticleBySlug(slug);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const relatedArticles = getRelatedBlogArticles(article);
  const pageUrl = `${SITE_URL}/blog/${article.slug}`;
  const imagePath = article.image || getBlogArticleImagePath(article.slug);
  const imageUrl = absoluteUrl(imagePath);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.description,
    datePublished: article.updatedAt,
    dateModified: article.updatedAt,
    author: {
      '@type': 'Organization',
      name: 'BuscaDoutor',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'BuscaDoutor',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    keywords: article.keywords.join(', '),
    image: [imageUrl],
    citation: article.sources.map((source) => source.url),
    articleSection: article.category === 'geral' ? 'Informações Gerais' : 'Especialidades Médicas',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Blog',
        item: `${SITE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: pageUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-article-page">
      <SEOHead
        title={`${article.title} | Blog BuscaDoutor`}
        description={article.description}
        canonicalUrl={`/blog/${article.slug}`}
        imageUrl={imagePath}
        pageType="article"
        structuredData={[schema, breadcrumbSchema]}
      />

      <Navbar />

      <main>
        <article>
          <header className="border-b border-[#D9E4E1] bg-[#F5FAF8]">
            <div className="container-app py-9 md:py-14">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-[#2D6D5D] mb-7"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao blog
              </Link>

              <div className="max-w-4xl">
                <p className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-primary">
                  {article.category === 'geral' ? 'Informações Gerais' : 'Especialidades Médicas'} / {article.specialty}
                </p>

                <h1 className="text-4xl md:text-5xl font-semibold text-[#1A2421] leading-tight mb-5">
                  {article.title}
                </h1>
                <p className="text-lg md:text-xl text-[#4F6861] leading-relaxed mb-6">
                  {article.description}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-[#59706A]">
                  <span className="inline-flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    Atualizado em {new Date(`${article.updatedAt}T12:00:00`).toLocaleDateString('pt-BR')}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {article.readingMinutes} min de leitura
                  </span>
                </div>
                <img
                  src={imagePath}
                  alt={`Imagem editorial do artigo: ${article.title}`}
                  width="1200"
                  height="630"
                  fetchPriority="high"
                  className="mt-8 aspect-[1200/630] w-full rounded-sm border border-[#D9E4E1] object-cover"
                />
              </div>
            </div>
          </header>

          <div className="container-app py-10 md:py-14">
            <div className="grid lg:grid-cols-[minmax(0,760px)_320px] gap-10 lg:gap-14 items-start">
              <div>
                <div className="mb-9 border-l-4 border-primary bg-[#F5FAF8] p-5 text-sm text-[#39564F] leading-relaxed">
                  <strong className="text-[#1A2421]">Nota editorial:</strong> Este conteúdo é voltado a
                  profissionais de saúde e tem finalidade informativa. Ele não substitui diretrizes locais,
                  julgamento clínico, consentimento, avaliação individual ou normas regulatórias aplicáveis.
                </div>

                <div className="space-y-9">
                  {article.sections.map((section) => (
                    <section key={section.heading}>
                      <h2 className="text-2xl md:text-[1.75rem] font-semibold text-[#1A2421] mb-3">
                        {section.heading}
                      </h2>
                      <p className="text-[1.03rem] text-[#425D56] leading-8">{section.body}</p>
                    </section>
                  ))}
                </div>

                <section className="mt-10 border-t border-[#D9E4E1] pt-7">
                  <h2 className="text-2xl font-semibold text-[#1A2421] mb-4">Fontes científicas consultadas</h2>
                  <ul className="space-y-3">
                    {article.sources.map((source) => (
                      <li key={source.url}>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-start gap-2 text-sm font-semibold text-primary hover:text-[#2D6D5D]"
                        >
                          <ExternalLink className="w-4 h-4 mt-0.5 flex-none" />
                          {source.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              <aside className="lg:sticky lg:top-24 space-y-8">
                <section className="border-t-4 border-primary bg-[#F5FAF8] p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary mb-3">Para quem é</p>
                  <p className="text-sm text-[#4F6861] leading-relaxed">{article.audience}</p>
                </section>

                <section className="border border-[#D9E4E1] p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary mb-4">Leia também</p>
                  <div className="divide-y divide-[#D9E4E1]">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.slug}
                        to={`/blog/${related.slug}`}
                        className="group block py-4 first:pt-0 last:pb-0"
                      >
                        <span className="text-sm font-semibold text-[#1A2421] group-hover:text-primary transition-colors">
                          {related.title}
                        </span>
                        <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                          Abrir artigo
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </section>
              </aside>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
